import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, useTheme } from '@mui/material';
import DataBox from '../../../Dashboard/DataBox';
import {
  FullSizeColumn,
  CenteredFlexBox,
  ColumnFlexBox,
} from '../../../../Components/FlexBox';
import CustomToggle from '../../../../Components/Inputs/CustomToggle';
import RedemptionGraphWidget from '../../Components/RedemptionGraphWidget';
import { ParentSize } from '@visx/responsive';
import LineChart from '../../../../Components/Charts/LineChart';
import { compareDates, formatListItemDate } from '../../../../Helpers/dates';
import TooltipContent from './TooltipContent';
import MultipleChipSelect from '../../../../Components/Inputs/MultipleChipSelect';
import { useAtomValue } from 'jotai';
import { userBranchesAtom, userDataAtom } from '../../../../Atoms';

const RollingGraphsWidget = function RollingGraphsWidget({
  userRedemptionData,
  branchRedemptionData,
  isOperator,
  minimumRedemptionCalls,
}) {
  const intl = useIntl();
  const theme = useTheme();

  const userData = useAtomValue(userDataAtom);
  const userBranches = useAtomValue(userBranchesAtom);
  const [tab, setTab] = useState('USER');
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);

  const handleChangeTab = (event, newTab) => {
    setTab(newTab);
  };

  const todayUserRedemptionData = userRedemptionData?.filter(
    ({ rollingDate }) => compareDates(new Date(rollingDate), new Date())
  );
  const todayBranchRedemptionData = branchRedemptionData?.filter(
    ({ rollingDate }) => compareDates(new Date(rollingDate), new Date())
  );

  const handleChangeUsers = (value) => {
    if (!Array.isArray(value) || value.length === 0) setUsers([]);
    else {
      setUsers(value);
    }
  };

  const handleChangeBranches = (value) => {
    if (!Array.isArray(value) || value.length === 0) setBranches([]);
    else {
      setBranches(value);
    }
  };

  const branchesOptions = [
    ...new Set(branchRedemptionData?.map(({ entity }) => entity)),
  ]
    ?.map((name) => ({ label: name, value: name }))
    ?.sort((a, b) => a.label.localeCompare(b.label));

  const usersOptions = [
    ...new Set(userRedemptionData?.map(({ entity }) => entity)),
  ]
    ?.map((name) => ({ label: name, value: name }))
    ?.sort((a, b) => a.label.localeCompare(b.label));

  useEffect(() => {
    if (isOperator) {
      setUsers(userData?.id ? [`${userData?.name} ${userData?.surname}`] : []);
      setBranches(
        userBranches?.length > 0
          ? [
              userBranches?.find(({ isDefault }) => isDefault)
                ?.branchDescription,
            ]
          : []
      );
    }
  }, [isOperator]);

  return (
    <FullSizeColumn overflow="hidden">
      <CenteredFlexBox justifyContent="center">
        <CustomToggle
          backgroundColor={
            theme.palette.mode === 'dark' ? 'greys.darker' : '#ededed'
          }
          thumbColor={theme.palette.mode === 'dark' ? '#111' : '#fff'}
          value={tab}
          handleChange={handleChangeTab}
          tabs={[
            { id: 'USER', label: 'USERS_REDEMPTION' },
            { id: 'BRANCH', label: 'BRANCHES_REDEMPTION' },
          ]}
          height="72px"
          width="40%"
          borderRadius="50px"
        />
      </CenteredFlexBox>
      <ColumnFlexBox
        sx={{ overflowY: 'auto', overflowX: 'hidden' }}
        gap="24px"
        padding="16px"
      >
        <DataBox
          flex="0 1 50%"
          title={
            tab === 'USER'
              ? intl.messages.USERS_REDEMPTION
              : intl.messages.BRANCHES_REDEMPTION
          }
          widget={
            <RedemptionGraphWidget
              redemptionData={
                tab === 'USER'
                  ? todayUserRedemptionData
                  : todayBranchRedemptionData
              }
              enableScroll={tab === 'USER'}
              noData={
                tab === 'USER'
                  ? todayUserRedemptionData?.length === 0 ||
                    todayUserRedemptionData?.[0]?.totalPractices === 0
                  : todayBranchRedemptionData?.length === 0 ||
                    todayBranchRedemptionData?.[0]?.totalPractices === 0
              }
              minimumRedemptionCalls={
                tab === 'USER' ? minimumRedemptionCalls : undefined
              }
            />
          }
        />
        <DataBox
          flex="0 1 50%"
          title={
            tab === 'USER'
              ? intl.messages.USERS_REDEMPTION
              : intl.messages.BRANCHES_REDEMPTION
          }
          widget={
            <ColumnFlexBox gap="16px">
              {!isOperator && (
                <CenteredFlexBox width="100%" justifyContent="flex-end">
                  <MultipleChipSelect
                    onChange={
                      tab === 'USER' ? handleChangeUsers : handleChangeBranches
                    }
                    values={tab === 'USER' ? users : branches}
                    options={tab === 'USER' ? usersOptions : branchesOptions}
                    size={'small'}
                    width="20%"
                  />
                </CenteredFlexBox>
              )}
              <Box
                sx={{
                  width: "100%",
                  height: "40vh"
                }}>
                <ParentSize>
                  {(parent) => {
                    const baseData =
                      tab === 'USER'
                        ? userRedemptionData
                        : branchRedemptionData;
                    const chartData = baseData?.map(
                      (
                        { rollingDate, totalPractices, totalAnsweredNum },
                        index
                      ) => ({
                        rollingDate,
                        practices: totalPractices,
                        answeredNum: totalAnsweredNum,
                        entity: 'Average',
                        index,
                      })
                    );

                    const selectedEntities = tab === 'USER' ? users : branches;

                    const entitiesData = selectedEntities.map((entity) => {
                      const entityMap = new Map(
                        baseData
                          ?.filter(
                            ({ entity: dataEntity }) => dataEntity === entity
                          )
                          ?.map(
                            ({
                              rollingDate,
                              practices,
                              answeredNum,
                              entity,
                            }) => [
                              rollingDate,
                              { practices, answeredNum, entity },
                            ]
                          )
                      );

                      return chartData?.map(({ rollingDate, index }) => ({
                        rollingDate,
                        practices: entityMap.get(rollingDate)?.practices,
                        answeredNum: entityMap.get(rollingDate)?.answeredNum,
                        entity,
                        index,
                      }));
                    });

                    return (
                      <LineChart
                        xTickFormat={(d) =>
                          /** Uses the index to access the corresponding date in chartData for formatting.
                           * If the data points are not evenly spaced, this approach assumes that the x-axis is linear and that the index corresponds to the position in the data array.
                           * If the x-axis were time-based, we would need to use the actual date values for formatting instead of relying on the index.
                           */
                          formatListItemDate(chartData[d]?.rollingDate)
                        }
                        yTickFormat={(v) => `${v}%`}
                        getXValue={(d) => d?.index} //uses index as x value for linear scale
                        getYValue={(d) =>
                          d?.practices && d?.answeredNum
                            ? (d?.practices / d?.answeredNum) * 100
                            : 0
                        }
                        width={parent.width}
                        height={parent.height}
                        data={[
                          chartData,
                          ...(entitiesData?.length > 0 &&
                          entitiesData[0]?.length > 0
                            ? entitiesData
                            : []),
                        ]}
                        colors={[
                          theme.palette.primary.light,
                          ...(theme.custom
                            .generateConsistentUniqueColors(
                              tab === 'USER'
                                ? usersOptions.map(({ value }) => value)
                                : branchesOptions.map(({ value }) => value)
                            )
                            ?.map((x) => x[theme.palette.mode]) ?? []),
                        ]}
                        numTicks={{ y: 5 }}
                        TooltipContent={TooltipContent}
                      />
                    );
                  }}
                </ParentSize>
              </Box>
            </ColumnFlexBox>
          }
        />
      </ColumnFlexBox>
    </FullSizeColumn>
  );
};

export default RollingGraphsWidget;
